/* LumetierHub — the editorial-maison front door at lumetiercreatives.com.
   Phase 1 revamp: full-bleed cinematic hero (bouquet on right, charcoal copy
   left), 4-product editorial catalog below, credibility strip, closing band
   and footer. Composed almost entirely from Phase 0 primitives. */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LumetierNav } from "@/pages/lumetier/Landing";
import usePageMeta from "@/hooks/usePageMeta";
import {
  ChapterEyebrow, DisplayHeadline, GoldRule, GoldFoilButton, HairlineButton,
  RotatedCaption, FilmGrain, Ticker, EditorialNumeral, MaisonIntro, ScrollScene,
} from "@/components/editorial";
import useMouseParallax from "@/hooks/useMouseParallax";
import { EDITORIAL_ASSETS } from "@/lib/editorialAssets";
import useEditorialReveal from "@/hooks/useEditorialReveal";
import { api } from "@/lib/api";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";
import useStructuredData from "@/hooks/useStructuredData";
import { lumetierOrganizationSchema, lumetierSoftwareSchema } from "@/lib/schemas";

const HERO_BG = "/hero/lumetier-hub-hero.jpg";

const PRODUCTS = [
  {
    chapter: "01",
    label: "Galleries",
    teaser: "A private viewing for every client.",
    to: "/lumetier/galleries",
    /* Soft-light editorial portrait — quiet, considered, in the spirit of a private viewing room. */
    image: "/lumetier/chapters/01-galleries.jpg",
  },
  {
    chapter: "02",
    label: "Website",
    teaser: "A public site as considered as the work.",
    to: "/lumetier/website",
    /* Editorial laptop/desk — the public face of a studio. */
    image: "/lumetier/chapters/02-website.jpg",
  },
  {
    chapter: "03",
    label: "Studio Manager",
    teaser: "The back of house, quietly choreographed.",
    to: "/lumetier/manager",
    /* Photographer's desk vignette — notebook, contact sheet, daylight. */
    image: "/lumetier/chapters/03-manager.jpg",
  },
  {
    chapter: "04",
    label: "Store",
    teaser: "Print sales as a print run.",
    to: "/lumetier/store",
    /* Linen-bound album / matted prints — the heirloom moment. */
    image: "/lumetier/chapters/04-store.jpg",
  },
];

export default function LumetierHub() {
  const [studios, setStudios] = useState([]);
  const [stats, setStats] = useState({ studios: null, galleries: null });
  useEditorialReveal();
  useStructuredData([lumetierOrganizationSchema(), lumetierSoftwareSchema()]);
  const parallax = useMouseParallax({ strength: 14, damping: 0.06 });

  usePageMeta({
    title: "Lumetier — A studio in a box for working photographers",
    description:
      "The hosted studio website, booking flow, gallery delivery, contracts, and dashboard for photographers — built by a photographer who got tired of patching five tools together.",
    canonical: "https://lumetiercreatives.com/",
  });

  useEffect(() => {
    api.get("/studios/featured").then((r) => setStudios(r.data || [])).catch(() => {});
    api.get("/lumetier/stats").then((r) => setStats(r.data || { studios: null, galleries: null })).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[color:var(--charcoal)] text-[color:var(--cream)] antialiased">
      <MaisonIntro surface="lumetier" />
      <LumetierNav />

      {/* HERO — full-bleed cinematic split */}
      <section className="relative min-h-[100vh] overflow-hidden flex items-center pt-16" data-testid="hub-hero">
        {/* Right-side bouquet, bleeds to the edge. Gradient fades into charcoal. */}
        <div className="absolute inset-y-0 right-0 left-0 lg:left-[34%]">
          <img
            src={HERO_BG}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0) scale(1.05)`,
              willChange: "transform",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--charcoal)] via-[color:var(--charcoal)]/65 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--charcoal)] via-transparent to-[color:var(--charcoal)]/30" />
        </div>
        <FilmGrain opacity={0.06} tone="cool" />

        {/* Left-edge rotated caption */}
        <div className="hidden lg:block absolute left-4 bottom-32 z-20">
          <RotatedCaption tone="gold">CHAPTER I — THE STUDIO</RotatedCaption>
        </div>

        <div className="relative z-10 max-w-[1480px] w-full mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <GoldRule width="2rem" />
            <div className="mt-3">
              <ChapterEyebrow chapter="I" label="The Studio" />
            </div>
            <DisplayHeadline size="cover" tone="charcoal" className="mt-7" data-reveal="rise">
              One quiet room for the whole <em>craft</em>.
            </DisplayHeadline>
            <p className="ed-body-lg mt-7 max-w-xl text-[color:var(--cream)]/70 leading-relaxed" data-reveal="rise">
              Sessions, galleries, contracts, your public site — every tool a photographer needs, designed with the editorial restraint your work already deserves.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4" data-reveal="rise">
              <GoldFoilButton href="#products">Open a product</GoldFoilButton>
              <HairlineButton href="/marketing" tone="dark">Why Lumetier</HairlineButton>
            </div>
          </div>
        </div>

        {/* Bottom ticker, on the hero edge */}
        <div className="absolute inset-x-0 bottom-0 z-20">
          <Ticker tone="dark" items={["NOW SERVING","ALLURE IMAGE — NJ","12 STUDIOS ONBOARDED","EST. 2026"]} />
        </div>
      </section>

      {/* PRODUCT CATALOG — 4 editorial product cards */}
      <section id="products" className="relative border-t border-[color:var(--gold-foil)]/12 py-24 lg:py-32" data-testid="hub-product-grid">
        <FilmGrain opacity={0.04} tone="cool" />
        <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 lg:mb-20">
            <div>
              <ChapterEyebrow chapter="❧" label="The Catalog" />
              <GoldRule width="3rem" className="mt-4 mb-6" />
              <DisplayHeadline size="section" tone="charcoal" data-reveal="rise">
                Four rooms. <em>One</em> studio.
              </DisplayHeadline>
            </div>
            <p className="ed-body-lg max-w-sm text-[color:var(--cream)]/65" data-reveal="rise">
              Sign in to any product. Your work, your settings, your clients — all share the same account and the same quiet design language.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 lg:gap-6">
            {PRODUCTS.map((p, i) => (
              <HubChapterCard key={p.chapter} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CINEMATIC SCENE STRIP — three full-bleed editorial spreads.
          Previously rendered as a sticky ScrollScene with a 210vh runway,
          which created ~1080px of empty black at the tail end of the
          wrapper (sticky child unpins ~halfway through). Static stacked
          spreads eliminate that dead air — every pixel shows imagery,
          and the scenes still read as a cinematic intermission between
          the catalog grid and the studio showcase. */}
      <section className="border-y border-[color:var(--gold-foil)]/12" data-testid="hub-scene-strip">
        {[
          {
            image: EDITORIAL_ASSETS.editorial_studio_wide.src,
            headline: "Galleries that don't just deliver",
            sub: "Lightning-fast uploads. Cinematic viewers. Original-quality downloads.",
          },
          {
            image: EDITORIAL_ASSETS.editorial_atelier_scene.src,
            headline: "Bookings that close themselves",
            sub: "Inquiries flow in. Calendars sync. Deposits clear. You stay behind the lens.",
          },
          {
            image: EDITORIAL_ASSETS.editorial_couple_warm.src,
            headline: "An experience clients remember",
            sub: "Interactive galleries, live sessions, private comments — the new way to deliver.",
          },
        ].map((scene, i) => (
          <div
            key={i}
            className="relative w-full h-[70vh] min-h-[520px] overflow-hidden"
            data-testid={`hub-scene-${i}`}
          >
            <img
              src={scene.image}
              alt=""
              aria-hidden="true"
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E0D0C]/85 via-[#0E0D0C]/35 to-[#0E0D0C]/15" />
            <div className="relative h-full max-w-[1480px] mx-auto px-6 lg:px-12 flex items-center">
              <div className="max-w-2xl">
                <div className="text-[10px] uppercase tracking-[0.42em] mb-4 text-[color:var(--gold-foil)]">
                  {`Scene ${String(i + 1).padStart(2, "0")} / 03`}
                </div>
                <h3
                  className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-[#F5EFE0]"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {scene.headline}
                </h3>
                {scene.sub && (
                  <p className="mt-5 text-sm sm:text-base text-[#F5EFE0]/75 max-w-xl leading-relaxed">
                    {scene.sub}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* FEATURED STUDIOS — editorial grid of tenants who opted in.
          Renders only when there's at least one featured studio so the
          marketing site never shows an empty section to early-stage prospects. */}
      {studios.length > 0 && <FeaturedStudiosSection studios={studios} />}      {/* CREDIBILITY STRIP */}
      <section className="border-t border-[color:var(--gold-foil)]/12 py-20 lg:py-28" data-testid="hub-credibility">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
          <ChapterEyebrow label="Trusted in quiet rooms" tone="gold" />
          <GoldRule width="2rem" className="mt-3 mb-12" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
            {[
              { v: "12", l: "Countries" },
              // Live-counted from /api/lumetier/stats. Falls back to "—" while
              // loading and to a short "0+" pluralization once we have the
              // count. Math.max so a freshly-seeded DB never shows literal "0".
              {
                v: stats.studios == null
                  ? "—"
                  : `${Math.max(stats.studios, 1)}${stats.studios >= 10 ? "+" : ""}`,
                l: "Studios on Lumetier",
              },
              {
                v: stats.galleries == null
                  ? "11k"
                  : stats.galleries >= 1000
                    ? `${(stats.galleries / 1000).toFixed(1).replace(/\.0$/, "")}k`
                    : `${stats.galleries}`,
                l: "Galleries delivered",
              },
              { v: "99.98%", l: "Uptime · last 90d" },
            ].map((s, i) => (
              <div key={s.l} className="text-left" data-testid={`hub-stat-${i}`} data-reveal="rise">
                <div className="font-serif text-5xl lg:text-6xl font-light tabular-nums text-[color:var(--cream)]">{s.v}</div>
                <div className="ed-eyebrow text-[color:var(--cream)]/55 mt-3">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING BAND */}
      <section className="relative border-t border-[color:var(--gold-foil)]/12 py-28 lg:py-40 overflow-hidden" data-testid="hub-closing">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,184,114,0.10),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12 text-center">
          <ChapterEyebrow label="Your studio — refined" />
          <GoldRule width="3rem" align="center" className="mt-4 mb-8" />
          <DisplayHeadline size="hero" tone="charcoal" align="center" className="max-w-3xl mx-auto">
            Less software. More <em>photography</em>.
          </DisplayHeadline>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <GoldFoilButton href="/admin/login">Sign in to your studio</GoldFoilButton>
            <HairlineButton href="/marketing" tone="dark">Read the full story</HairlineButton>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[color:var(--gold-foil)]/12 pt-20 pb-10" data-testid="hub-footer">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-4 gap-12 mb-16">
            <div className="lg:col-span-2 max-w-md">
              <div className="font-serif text-3xl text-[color:var(--cream)]" style={{letterSpacing: "0.42em"}}>LUMETIER</div>
              <p className="ed-body-lg mt-5 text-[color:var(--cream)]/60 leading-relaxed">
                The quiet operating system for photographers — built by photographers, designed like the work it serves.
              </p>
            </div>
            <FooterCol
              title="Catalog"
              links={[
                { to: "/lumetier/galleries", label: "01 — Galleries" },
                { to: "/lumetier/website",   label: "02 — Website" },
                { to: "/lumetier/manager",   label: "03 — Studio Manager" },
                { to: "/lumetier/store",     label: "04 — Store" },
              ]}
            />
            <FooterCol
              title="Lumetier"
              links={[
                { to: "/marketing", label: "Why Lumetier", testid: "hub-footer-marketing" },
                { to: "/marketing#pricing", label: "Pricing" },
                { to: "/onboard", label: "Apply for an invite" },
                { to: "/admin/login", label: "Sign in" },
                { to: "/lumetier-preview", label: "Brand book" },
              ]}
            />
          </div>
          <div className="border-t border-[color:var(--gold-foil)]/12 pt-6 flex flex-wrap items-center justify-between gap-4 ed-eyebrow text-[color:var(--cream)]/45">
            <span>© {new Date().getFullYear()} LUMETIER CREATIVES</span>
            <span>STATUS · 99.98%</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="ed-eyebrow text-[color:var(--gold-base)] mb-5">{title}</div>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            <Link to={l.to} className="text-[color:var(--cream)]/80 hover:text-[color:var(--gold-foil)] transition-colors text-sm" data-testid={l.testid}>
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* HubChapterCard — editorial product tile w/ background imagery + GPU-only
   hover. The image scales 1 → 1.04 on hover; arrow translates X; a thin gold
   hairline grows from bottom-left (scaleX 0 → 1). All transforms/opacity
   only — no layout-property animations (the Edge/Windows perf lesson). */
function HubChapterCard({ product: p, index }) {
  return (
    <motion.div
      data-reveal="rise"
      style={{ animationDelay: `${index * 120}ms` }}
      initial="rest"
      whileHover="hover"
      animate="rest"
      className="relative"
    >
      <Link
        to={p.to}
        data-testid={`hub-tile-${p.chapter}`}
        className="group relative block overflow-hidden p-7 lg:p-10 min-h-[300px] border border-[color:var(--gold-foil)]/15 hover:border-[color:var(--gold-foil)]/55 bg-[color:var(--charcoal-soft)]/40 flex flex-col justify-between transition-colors duration-300"
      >
        {/* Background image, full-bleed, behind everything else.
            Composited via transform scale only — no paint-triggering filters. */}
        <motion.div
          className="absolute inset-0 -z-10"
          variants={{ rest: { scale: 1 }, hover: { scale: 1.04 } }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ willChange: "transform" }}
        >
          <img
            src={p.image}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(20%) brightness(0.5)" }}
          />
          {/* Charcoal gradient overlay — keeps text legible regardless of image. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(14,13,12,0.92) 0%, rgba(14,13,12,0.78) 50%, rgba(14,13,12,0.95) 100%)",
            }}
          />
        </motion.div>

        {/* Hairline that grows from the bottom-left on hover. */}
        <motion.span
          aria-hidden="true"
          className="absolute left-0 bottom-0 h-px bg-[color:var(--gold-foil)] origin-left"
          variants={{ rest: { scaleX: 0 }, hover: { scaleX: 1 } }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: "100%", willChange: "transform" }}
        />

        <div className="relative flex items-start justify-between gap-4">
          <motion.div
            variants={{ rest: { y: 0 }, hover: { y: -2 } }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <EditorialNumeral value={p.chapter} size="lg" style="outline" tone="gold" />
          </motion.div>
          <span className="ed-eyebrow text-[color:var(--gold-base)] mt-3">{`Product ${p.chapter}`}</span>
        </div>

        <div className="relative mt-10">
          <DisplayHeadline as="h3" size="sub" tone="charcoal" className="transition-colors group-hover:text-[color:var(--gold-foil)]">
            {p.label}
          </DisplayHeadline>
          <p className="ed-body-lg mt-2 text-[color:var(--cream)]/75 max-w-md">{p.teaser}</p>
          <div className="mt-5 inline-flex items-center gap-2 ed-eyebrow text-[color:var(--gold-foil)]">
            Open chapter
            <motion.span
              aria-hidden="true"
              variants={{ rest: { x: 0 }, hover: { x: 4 } }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >→</motion.span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

/* FeaturedStudiosSection — editorial grid of tenants who opted-in to be
   featured on the public Lumetier hub. Renders a chapter eyebrow + headline,
   then a responsive grid of studio cards (name + location + template + hero
   image + backlink to their public site). Founding studio (Allure) bubbles
   to the front automatically via the backend sort. */
function FeaturedStudiosSection({ studios }) {
  return (
    <section
      className="relative border-t border-[color:var(--gold-foil)]/12 py-24 lg:py-32"
      data-testid="hub-featured-studios"
    >
      <FilmGrain opacity={0.04} tone="cool" />
      <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 lg:mb-20">
          <div>
            <ChapterEyebrow chapter="❧" label="Studios on Lumetier" />
            <GoldRule width="3rem" className="mt-4 mb-6" />
            <DisplayHeadline size="section" tone="charcoal" data-reveal="rise">
              In <em>good</em> company.
            </DisplayHeadline>
          </div>
          <p className="ed-body-lg max-w-sm text-[color:var(--cream)]/65" data-reveal="rise">
            {studios.length} working {studios.length === 1 ? "studio" : "studios"} already {studios.length === 1 ? "trusts" : "trust"} Lumetier with their public site, bookings, and galleries. A small editorial sample below.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {studios.slice(0, 12).map((studio, i) => (
            <FeaturedStudioCard key={studio.studio_id} studio={studio} index={i} />
          ))}
          {/* Ghost placeholders — when the visible studio count is < 3 we
              pad the row with subtle "Your studio could be next" tiles so
              the section feels intentional instead of empty. Each ghost
              links to /admin/login (the onboarding entry) and doubles as a
              soft signup CTA. Suppressed once we have 3+ real studios so
              we never push real tenants below the fold. */}
          {studios.length < 3 && Array.from({ length: 3 - studios.length }).map((_, gi) => (
            <a
              key={`ghost-${gi}`}
              href="/admin/login"
              data-testid={`hub-featured-studio-ghost-${gi}`}
              className="group relative block aspect-[5/4] border border-dashed border-[color:var(--gold-foil)]/25 hover:border-[color:var(--gold-foil)]/55 bg-[color:var(--charcoal-soft)]/20 transition-colors duration-300 overflow-hidden"
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--gold-foil)]/70 mb-3">
                  Studio {String(studios.length + gi + 1).padStart(2, "0")}
                </div>
                <div className="font-serif text-xl lg:text-2xl text-[color:var(--cream)]/55 leading-tight mb-4 max-w-[18ch]">
                  Your studio<br />could be next.
                </div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-foil)] border-b border-[color:var(--gold-foil)]/40 group-hover:border-[color:var(--gold-foil)] pb-0.5 transition-colors">
                  Reserve a chapter →
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* FeaturedStudioCard — single editorial tile for a featured studio. Links
   to the studio's live URL (custom domain when active, /s/<slug> otherwise).
   Opens in a new tab so the marketing site retains its place in the user's
   tab strip. Visually mirrors HubChapterCard but with the studio's actual
   hero image and a "FOUNDER" badge for Allure. */
function FeaturedStudioCard({ studio, index }) {
  const href = studio.live_url || `/s/${studio.slug}`;
  const isExternal = Boolean(studio.live_url);
  const heroImg = studio.hero_image
    || EDITORIAL_ASSETS.editorial_studio_wide.src;

  // template_id pretty-label for the eyebrow. Covers all 10 shipped
  // templates so v2 picks (Atelier, Brutalist, etc) don't silently
  // collapse to "Editorial" on the marketing showcase.
  const TEMPLATE_LABELS = {
    editorial: "Editorial",
    bold: "Bold",
    minimal: "Minimal",
    journal: "Journal",
    atelier: "Atelier",
    heirloom: "Heirloom",
    vista: "Vista",
    brutalist: "Brutalist",
    spotlight: "Spotlight",
    botanical: "Botanical",
  };
  const templateLabel = TEMPLATE_LABELS[studio.template_id] || "Editorial";

  return (
    <motion.a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer external dofollow" : undefined}
      data-testid={`hub-featured-studio-${studio.slug}`}
      data-reveal="rise"
      style={{ animationDelay: `${index * 80}ms` }}
      initial="rest"
      whileHover="hover"
      animate="rest"
      className="group relative block overflow-hidden border border-[color:var(--gold-foil)]/15 hover:border-[color:var(--gold-foil)]/55 bg-[color:var(--charcoal-soft)]/40 transition-colors duration-300"
    >
      {/* Hero photo with subtle hover zoom */}
      <motion.div
        className="relative aspect-[5/4] overflow-hidden"
        variants={{ rest: { scale: 1 }, hover: { scale: 1.03 } }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ willChange: "transform" }}
      >
        {heroImg ? (
          <img
            src={optimizeImageUrl(heroImg, { w: 900 })}
            srcSet={optimizedSrcSet(heroImg, [500, 700, 900, 1200]) || undefined}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(8%) brightness(0.85)" }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--charcoal)] via-[color:var(--charcoal-soft)] to-[color:var(--charcoal)]" />
        )}
        {/* Charcoal gradient floor for legibility on dense images */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(14,13,12,0.88) 0%, rgba(14,13,12,0.30) 45%, rgba(14,13,12,0.15) 100%)",
          }}
          aria-hidden
        />
        {/* Founder badge — only the founding studio */}
        {studio.is_founder && (
          <div className="absolute top-4 left-4 ed-eyebrow text-[color:var(--gold-foil)] bg-[color:var(--charcoal)]/70 backdrop-blur-sm px-3 py-1.5 border border-[color:var(--gold-foil)]/45">
            Founding Studio
          </div>
        )}
      </motion.div>

      {/* Caption below */}
      <div className="relative px-6 py-5 lg:px-7 lg:py-6 border-t border-[color:var(--gold-foil)]/12">
        <div className="flex items-baseline justify-between gap-3">
          <div className="ed-eyebrow text-[color:var(--gold-base)]">{templateLabel} · Lumetier</div>
          {studio.location && (
            <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--cream)]/55 truncate ml-3">
              {studio.location}
            </div>
          )}
        </div>
        <h3 className="mt-3 font-serif text-2xl lg:text-3xl font-light leading-tight text-[color:var(--cream)] transition-colors group-hover:text-[color:var(--gold-foil)]">
          {studio.name}
        </h3>
        {studio.tagline && (
          <p className="mt-2 text-sm italic text-[color:var(--cream)]/65 font-serif line-clamp-2">
            {studio.tagline}
          </p>
        )}
        <div className="mt-4 inline-flex items-center gap-2 ed-eyebrow text-[color:var(--gold-foil)]">
          {isExternal ? "Open site" : "View site"}
          <motion.span
            aria-hidden="true"
            variants={{ rest: { x: 0 }, hover: { x: 4 } }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >→</motion.span>
        </div>
      </div>
    </motion.a>
  );
}
